import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import './scss/AIInsightsPanel.scss';
import { Close } from '../icons';

const AIInsightsPanel = ({ boardId }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [expandedInsight, setExpandedInsight] = useState(null);

  useEffect(() => {
    if (boardId) {
      fetchInsights();
    }
  }, [boardId]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${config.API_URI}/v1/ai/insights/${boardId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch insights');
      }

      const data = await response.json();
      if (data.success) {
        setInsights(data.insights || []);
      } else {
        throw new Error(data.error || 'Failed to fetch insights');
      }
    } catch (err) {
      console.error('Insights fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateNewInsights = async () => {
    try {
      setGenerating(true);
      setError(null);

      const response = await fetch(`${config.API_URI}/v1/ai/insights/generate/${boardId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate insights');
      }

      const data = await response.json();
      if (data.success) {
        setInsights(data.insights || []);
      } else {
        throw new Error(data.error || 'Failed to generate insights');
      }
    } catch (err) {
      console.error('Insights generation error:', err);
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const dismissInsight = async (insightId) => {
    try {
      const response = await fetch(`${config.API_URI}/v1/ai/insights/${insightId}/dismiss`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: 'User dismissed'
        }),
      });

      if (response.ok) {
        setInsights(prev => prev.filter(insight => insight._id !== insightId));
      }
    } catch (err) {
      console.error('Dismiss insight error:', err);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'check_circle';
      default: return 'lightbulb';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getInsightTypeLabel = (type) => {
    switch (type) {
      case 'productivity_trend': return 'Productivity Trend';
      case 'bottleneck_detection': return 'Bottleneck Detection';
      case 'team_load_balancing': return 'Team Load Balancing';
      case 'completion_prediction': return 'Completion Prediction';
      case 'goal_progress': return 'Goal Progress';
      default: return 'AI Insight';
    }
  };

  if (loading) {
    return (
      <div className="ai-insights-panel">
        <div className="insights-loading">
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>Analyzing board...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-insights-panel">
      <div className="insights-header">
        <h2>AI Insights</h2>
        <button
          onClick={generateNewInsights}
          disabled={generating}
          className="generate-btn"
          data-tooltip-content="Generate Insights"
          data-tooltip-position="top"
        >
          {generating ? (
            <div className="btn-spinner">
              <span></span>
              <span></span>
              <span></span>
            </div>
          ) : (
            <span className="material-symbols-outlined">auto_awesome</span>
          )}
        </button>
      </div>

      {error && (
        <div className="insights-error">
          <p>{error}</p>
          <button onClick={fetchInsights} className="retry-btn">
            <span className="material-symbols-outlined">refresh</span>
            Try Again
          </button>
        </div>
      )}

      {!error && insights.length === 0 && !loading && (
        <div className="insights-empty">
          <p>No insights yet. Click generate to analyze your board.</p>
        </div>
      )}

      {insights.length > 0 && (
        <div className="insights-list">
          {insights.map((insight) => (
            <div
              key={insight._id}
              className={`insight-card ${insight.severity}`}
            >
              <div className="insight-header">
                <div className="insight-title-row">
                  <span className={`severity-badge ${insight.severity}`}>
                    {insight.severity}
                  </span>
                  <span className="insight-type">
                    {getInsightTypeLabel(insight.insightType)}
                  </span>
                </div>
                <div className="insight-actions">
                  <button
                    onClick={() => setExpandedInsight(
                      expandedInsight === insight._id ? null : insight._id
                    )}
                    className="action-btn"
                  >
                    <span className="material-symbols-outlined">
                      {expandedInsight === insight._id ? 'expand_less' : 'expand_more'}
                    </span>
                  </button>
                  <button
                    onClick={() => dismissInsight(insight._id)}
                    className="action-btn"
                  >
                    <Close size={20} />

                  </button>
                </div>
              </div>

              <div className="insight-content">
                <h4>{insight.title}</h4>
                <p className="insight-description">{insight.description}</p>

                {expandedInsight === insight._id && (
                  <div className="insight-details">
                    {insight.recommendations && insight.recommendations.length > 0 && (
                      <div className="recommendations-section">
                        <h5>Recommendations</h5>
                        <ul className="recommendations-list">
                          {insight.recommendations.map((rec, index) => (
                            <li key={index} className="recommendation-item">
                              <span className={`priority-dot ${rec.priority}`}></span>
                              {rec.action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {insight.data && (
                      <div className="data-section">
                        <h5>Data</h5>
                        <div className="data-grid">
                          {Object.entries(insight.data).map(([key, value]) => (
                            <div key={key} className="data-item">
                              <span className="data-label">
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              </span>
                              <span className="data-value">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="insight-meta">
                      <span className="meta-item">
                        Confidence: {Math.round(insight.confidence * 100)}%
                      </span>
                      <span className="meta-item">
                        {new Date(insight.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIInsightsPanel;
